<template>
    <nav v-if="activeNavigationSection" class="top-bar-current-section" :title="breadcrumbTitle" :aria-label="$t('Breadcrumb')">
        <router-link to="/" class="top-bar-current-section-icon" :title="$t('Go to home')" :aria-label="$t('Go to home')">
            <i class="fas fa-home"></i>
        </router-link>
        <i class="fa-solid fa-chevron-right chevron-icon" />
        <NavLink
            v-if="sectionCrumbTarget"
            :to="sectionCrumbTarget"
            :text="activeNavigationSection.title"
            class="top-bar-current-section-text"
        />
        <span v-else class="top-bar-current-section-text">
            <span>{{ activeNavigationSection.title }}</span>
        </span>
        <template v-if="activeNavigationItem">
            <i class="fa-solid fa-chevron-right chevron-icon" />
            <NavLink v-if="itemCrumbTarget" :to="itemCrumbTarget" :text="activeNavigationItem.title" class="top-bar-current-section-text" />
            <span v-else class="top-bar-current-section-text">
                <span>{{ activeNavigationItem.title }}</span>
            </span>
        </template>
    </nav>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { AuthController } from "@/control/auth";
import NavLink from "@/components/navigation/NavLink.vue";
import { buildSidebarNavigation, getActiveSidebarItem, getActiveSidebarSection } from "@/utils/sidebar-navigation";
import type { SidebarNavigationItem, SidebarNavigationSection } from "@/utils/sidebar-navigation";

export default defineComponent({
    components: {
        NavLink,
    },
    name: "NavigationBreadcrumb",
    data() {
        return {
            navigationVersion: 0,
        };
    },
    computed: {
        navigationSections: function (): SidebarNavigationSection[] {
            void this.navigationVersion;

            return buildSidebarNavigation({
                $t: (key: string) => this.$t(key),
                isAuthenticated: AuthController.isAuthenticated(),
                hasPermission: (permission: string) => AuthController.hasPermission(permission),
                clearingHouseEnabled: AuthController.ClearingHouseEnabled,
                federatedLearningEnabled: AuthController.FederatedLearningEnabled,
                useAppMarketplaces: AuthController.UseAppMarketplaces,
                isAppMarketplace: AuthController.IsAppMarketplace,
            });
        },

        activeNavigationSection: function (): SidebarNavigationSection | null {
            return getActiveSidebarSection(this.currentRouteName, this.navigationSections);
        },

        activeNavigationItem: function (): SidebarNavigationItem | null {
            return getActiveSidebarItem(this.currentRouteName, this.navigationSections);
        },

        currentRouteName: function (): string {
            return this.$route?.name ? String(this.$route.name) : "";
        },

        sectionCrumbTarget: function (): string {
            const routeName = this.activeNavigationSection?.route;
            if (!routeName || routeName === this.currentRouteName) return "";

            return this.$router.resolve({ name: routeName }).path;
        },

        itemCrumbTarget: function (): string {
            const routeName = this.activeNavigationItem?.route;
            if (!routeName || routeName === this.currentRouteName) return "";

            return this.$router.resolve({ name: routeName }).path;
        },

        breadcrumbTitle: function (): string {
            if (!this.activeNavigationSection) return "";
            if (!this.activeNavigationItem) return this.activeNavigationSection.title;

            return `${this.activeNavigationSection.title} > ${this.activeNavigationItem.title}`;
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", () => {
            this.navigationVersion += 1;
        });
    },
});
</script>
